import { mapFlags } from '@/features/calendar/adapters/calendarAdapters';
import type {
  Appointment,
  AppointmentDossier,
  AppointmentInsuranceDossier,
  FamilyOverviewMember,
  InsuranceEstimate,
  OpportunityItem,
} from '@elva/ui';
import type {
  AppointmentDetailInsuranceWire,
  AppointmentDetailOpportunityModeWire,
  AppointmentDetailWire,
  AppointmentEligibilityStatus,
} from '../api/types';
import { mapFlagIcon } from './appointmentAdapters';

/** Server display status → the calendar's status vocabulary (header label + tone). */
const CALENDAR_STATUS: Record<string, Appointment['status']> = {
  CONFIRMED: 'CONFIRMED',
  PENDING: 'PENDING',
  SCHEDULED: 'SCHEDULED',
  ARRIVED: 'ARRIVED',
  CHECKED_IN: 'CHECKED_IN',
  SEATED: 'TREATMENT',
  TREATMENT: 'TREATMENT',
  TREATMENT_DONE: 'COMPLETED',
  CHECKED_OUT: 'COMPLETED',
  COMPLETED: 'COMPLETED',
  NO_SHOW: 'NO_SHOW',
  CANCELLED: 'BROKEN',
  BROKEN: 'BROKEN',
  RESCHEDULED: 'RESCHED',
  RESCHED: 'RESCHED',
};

export interface AppointmentDetailEligibility {
  status: AppointmentEligibilityStatus;
  lastChecked?: string;
  stale: boolean;
}

export interface AdaptedAppointmentDetail {
  /** Calendar-shaped appointment that drives the modal header, time and production. */
  appointment: Appointment;
  dossier: AppointmentDossier;
  insurance: AppointmentInsuranceDossier;
  providerName: string;
  patientId: string;
  /** Server display status — matches the change-status menu values where applicable. */
  status: string;
  operatoryId: string | null;
  eligibility: AppointmentDetailEligibility;
}

export interface AdaptAppointmentDetailOptions {
  /** Workspace display name for the current operatory; falls back to the detail's short name. */
  operatoryDisplayName?: string | undefined;
}

/** HH:MM in the ISO string is already the clinic's local time (offset is included). */
function localHour(iso: string): number {
  const match = /T(\d{2}):(\d{2})/.exec(iso);
  if (!match) return 0;
  return Number(match[1]) + Number(match[2]) / 60;
}

function durationHours(startsAt: string, endsAt: string): number {
  const diff = (Date.parse(endsAt) - Date.parse(startsAt)) / 3_600_000;
  return Number.isFinite(diff) && diff > 0 ? diff : 1;
}

function formatInTimezone(iso: string, timeZone: string | undefined): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  try {
    return date.toLocaleString(undefined, { ...options, ...(timeZone ? { timeZone } : {}) });
  } catch {
    return date.toLocaleString(undefined, options);
  }
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})/;

function ageFromDateOfBirth(dateOfBirth: string): number | null {
  const match = DATE_ONLY.exec(dateOfBirth);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const now = new Date();
  let age = now.getFullYear() - year;
  const beforeBirthday =
    now.getMonth() + 1 < month || (now.getMonth() + 1 === month && now.getDate() < day);
  if (beforeBirthday) age -= 1;
  return age >= 0 ? age : null;
}

function formatPatientSince(date: string): string | null {
  const match = DATE_ONLY.exec(date);
  if (!match) return null;
  // Local-date constructor — `new Date('YYYY-MM-DD')` parses as UTC and can land on the prior month.
  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return `Patient since ${parsed.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`;
}

function buildDemographics(patient: AppointmentDetailWire['patient']): string {
  const age = ageFromDateOfBirth(patient.dateOfBirth);
  return [
    patient.sex !== 'Unknown' ? patient.sex : null,
    age != null ? String(age) : null,
    patient.patientSince ? formatPatientSince(patient.patientSince) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function parseDollars(display: string): number | null {
  const value = Number.parseFloat(display.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(value) ? value : null;
}

function stripLeadingAmount(portion: string, amount: string): string {
  const trimmed = portion.trim();
  const rest = trimmed.startsWith(amount) ? trimmed.slice(amount.length) : trimmed;
  return rest.replace(/^\s*[—–-]\s*/, '').trim();
}

function buildOpportunities(
  window: AppointmentDetailWire['opportunityWindow'] | undefined,
): OpportunityItem[] {
  const modes: [string, AppointmentDetailOpportunityModeWire | undefined][] = [
    ['walkin', window?.walkin],
    ['walkout', window?.walkout],
  ];
  const seen = new Set<string>();
  const items: OpportunityItem[] = [];

  const push = (item: OpportunityItem) => {
    if (seen.has(item.title)) return;
    seen.add(item.title);
    items.push(item);
  };

  for (const [mode, section] of modes) {
    if (!section) continue;
    // Playbook headlines may still be generating — the TL;DR bullets stand in until they land.
    if (section.headlines.length > 0) {
      section.headlines.forEach((h) => {
        push({
          id: `${mode}-${h.id}`,
          icon: mapFlagIcon(h.icon),
          accent: h.source === 'elva' ? 'var(--gold)' : 'var(--ai-3)',
          title: h.headline,
          description: h.detail,
        });
      });
      continue;
    }
    section.summary.forEach((line, index) => {
      push({
        id: `${mode}-summary-${String(index)}`,
        icon: 'sparkle',
        accent: 'var(--ai-3)',
        title: line,
        description: '',
      });
    });
  }
  return items;
}

function buildEstimate(
  insurance: AppointmentDetailInsuranceWire | null | undefined,
): InsuranceEstimate | undefined {
  const collect = insurance?.collectToday;
  if (!insurance || !collect?.amount) return undefined;
  const patientToday = parseDollars(collect.amount);
  if (patientToday == null) return undefined;
  const note = stripLeadingAmount(collect.portion, collect.amount);
  return {
    carrier: insurance.planLabel,
    patientToday,
    ...(note ? { patientNote: note } : {}),
    procedures: [],
  };
}

function buildFamily(family: AppointmentDetailWire['family'] | undefined): FamilyOverviewMember[] {
  return (family ?? []).map((member) => ({
    id: member.patientId ?? member.name,
    name: member.name,
    relation: member.rel,
    recallLabel: member.status,
    recallStatus: member.overdue ? 'overdue' : 'on_schedule',
    recallNote: '',
  }));
}

function buildEligibility(
  insurance: AppointmentDetailInsuranceWire | null | undefined,
  timeZone: string,
): AppointmentDetailEligibility {
  const eligibility = insurance?.eligibility;
  return {
    status: eligibility?.status ?? 'unknown',
    ...(eligibility?.lastCheckedAt
      ? { lastChecked: formatInTimezone(eligibility.lastCheckedAt, timeZone) }
      : {}),
    stale: eligibility?.stale ?? false,
  };
}

function buildInsuranceDossier(wire: AppointmentDetailWire): AppointmentInsuranceDossier {
  const estimate = buildEstimate(wire.insurance);
  return {
    opportunities: buildOpportunities(wire.opportunityWindow),
    ...(estimate ? { estimate } : {}),
    family: buildFamily(wire.family),
  };
}

export function adaptAppointmentDetail(
  wire: AppointmentDetailWire,
  options: AdaptAppointmentDetailOptions = {},
): AdaptedAppointmentDetail {
  const { appointment, patient, provider, operatory, insurance } = wire;
  const timeZone = appointment.timezone;

  const calendarAppointment: Appointment = {
    id: appointment.id,
    providerId: provider?.id ?? '',
    hour: localHour(appointment.startsAt),
    duration: durationHours(appointment.startsAt, appointment.endsAt),
    patient: patient.fullDisplayName,
    status: CALENDAR_STATUS[appointment.status.toUpperCase()] ?? 'SCHEDULED',
    production: appointment.production / 100,
    procedure: appointment.procedure,
    flags: mapFlags(appointment.flags),
  };

  const dossier: AppointmentDossier = {
    demographics: buildDemographics(patient),
    operatory: options.operatoryDisplayName ?? operatory?.name ?? '—',
    kioskCode: wire.kioskCode ?? '—',
    clinicalAlerts: {
      allergies: wire.clinicalAlerts.allergies,
      conditions: wire.clinicalAlerts.conditions,
      medications: wire.clinicalAlerts.medications,
      mindful: wire.clinicalAlerts.mindful,
    },
    opportunityWindow: {
      walkin: toOpportunityMode(wire.opportunityWindow.walkin),
      walkout: toOpportunityMode(wire.opportunityWindow.walkout),
    },
    insurance: {
      planLabel: insurance?.planLabel ?? '',
      tellPatient: insurance?.sayThis ?? '',
      collectToday: insurance?.collectToday?.portion ?? '',
      handled: (insurance?.needsAction ?? []).filter((n) => n.kind === 'info').map((n) => n.text),
      todo: (insurance?.needsAction ?? []).filter((n) => n.kind === 'warning').map((n) => n.text),
    },
    doctorsNote: wire.doctorsNote ?? '',
    lastInteraction: wire.lastInteraction ?? '',
    family: wire.family.map((member) => ({
      name: member.name,
      rel: member.rel,
      status: member.status,
      overdue: member.overdue,
    })),
    comfort: wire.comfort,
    patientValue: wire.patientValue.map((row) =>
      row.tone === 'warning' || row.tone === 'green' || row.tone === 'ink'
        ? { label: row.label, value: row.value, tone: row.tone }
        : { label: row.label, value: row.value },
    ),
    notes: wire.notes.map((note) => ({
      author: note.author,
      when: formatInTimezone(note.createdAt, timeZone),
      text: note.text,
      mine: note.mine,
    })),
  };

  return {
    appointment: calendarAppointment,
    dossier,
    insurance: buildInsuranceDossier(wire),
    providerName: provider?.name ?? 'Unassigned',
    patientId: patient.id,
    status: appointment.status,
    operatoryId: operatory?.id ?? null,
    eligibility: buildEligibility(insurance, timeZone),
  };
}

function toOpportunityMode(
  section: AppointmentDetailOpportunityModeWire,
): AppointmentDossier['opportunityWindow']['walkin'] {
  return {
    summary: section.summary,
    headlines: section.headlines.map((h) => ({
      id: h.id,
      icon: mapFlagIcon(h.icon),
      source: h.source,
      headline: h.headline,
      detail: h.detail,
    })),
  };
}
